import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import useTranslation from 'next-translate/useTranslation';
import dynamic from 'next/dynamic';
import Image from 'next/image';

import {
  Button,
  Checkbox,
  Grid,
  Input,
  Prose,
  Select,
  TextArea,
} from '@/atoms';

import CheckmarkCircle from '@/assets/icons/CheckmarkCircle.svg';
import WarningCircle from '@/assets/icons/WarningCircle.svg';
import { gtmPush, sha256 } from '@/utils';

import type { FunctionComponent } from 'react';
import type { TypeContactFormProps } from './types';

const PhoneInput = dynamic(() => import('@/atoms/phone-input'), {
  ssr: false,
});

const PopUp = dynamic(() => import('@/atoms/pop-up-base'), {
  ssr: false,
});

type TypeFormInputs = {
  name: string;
  lastName: string;
  email: string;
  emailConfirm: string;
  telephone: string;
  hearUs: string;
  building: string;
  accommodation: string;
  addMore?: string;
  checkboxMandatory: boolean;
  checkboxNotMandatory: boolean;
};

/**
 * ContactForm
 */
export const ContactForm: FunctionComponent<TypeContactFormProps> = ({
  textPolicies,
  buildingSelect,
  accommodationSelect,
  hearUsSelect,
  className,
  landing = false,
  defaultBuilding,
  defaultApartment,
  hearUsDefaultValue,
}: TypeContactFormProps) => {
  const { t, lang } = useTranslation('common');

  const buildingsSelectValues = buildingSelect.map((building) => ({
    label: building.label,
    value: building.id,
    disabled: building.disabled,
  }));

  const mapOfAccommodation = new Map(accommodationSelect);
  const methods = useForm<TypeFormInputs>();

  const watchBuilding = methods.watch(
    'building',
    buildingsSelectValues[0].value.toString()
  );
  const [showModal, setShowModal] = useState(false);
  const [hasError, setHasError] = useState(false);
  const Icon = hasError ? WarningCircle : CheckmarkCircle;
  const close = () => {
    setShowModal(false);
    setHasError(false);
  };
  const onSubmit = async (data: TypeFormInputs) => {
    const isMobilePhone = (await import('validator/lib/isMobilePhone')).default;

    const valid = isMobilePhone(data?.telephone ? data?.telephone : '123');

    if (!valid) {
      methods.setError('telephone', {
        type: 'custom',
        message: t('components.organisms.contactForm.phoneError'),
      });
      return;
    }

    if (data.email !== data.emailConfirm) {
      methods.setError('emailConfirm', {
        type: 'custom',
        message: t('components.organisms.contactForm.emailError'),
      });
      return;
    } else {
      try {
        const selectedBuilding = buildingSelect.find(
          (building) => building.id.toString() === data.building
        );
        const response = await fetch('/api/lavanda', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: data.name,
            lastName: data.lastName,
            email: data.email,
            phone: `+${data.telephone}`,
            description: data?.addMore ?? 'No se ha añadido descripción',
            marketingOptIn: data.checkboxNotMandatory,
            gdprConsent: true,
            hearUs: data.hearUs,
            building: {
              name: selectedBuilding?.label,
              id: selectedBuilding?.endpointId,
            },
            typeOfApartment: data.accommodation,
          }),
        });
        const body = await response.json();

        if (response.ok) {
          gtmPush({
            event: 'generate_lead',
            lead_type: 'contact',
            provincia: '',
            phone_number: await sha256(`+${data.telephone}`),
            edificio: selectedBuilding?.label,
            tipo_de_apartamento: data.accommodation,
            conocido: data.hearUs,
            address: {
              first_name: await sha256(data.name),
              last_name: await sha256(data.lastName),
            },
            email: await sha256(data.email),
          });

          setShowModal(true);

          methods.clearErrors();
          methods.reset();
        } else throw new Error(JSON.stringify(body));
      } catch (e) {
        setHasError(true);
        setShowModal(true);
      }
    }
  };

  return (
    <>
      <FormProvider {...methods}>
        <form
          className={`
          relative mt-6 flex w-full flex-col gap-4 rounded-md bg-white p-6 pt-10 shadow-md lg:p-16 
          ${landing ? 'lg:w-2/5' : ''}
          ${className}
        `}
          onSubmit={methods.handleSubmit(onSubmit)}
        >
          <div
            className={`${
              landing
                ? 'flex flex-row items-center justify-center'
                : 'absolute left-0 top-0 -mt-8 ml-6 lg:ml-16'
            }`}
          >
            <Image
              src="/assets/img/logo/logo-brown.svg"
              alt="logo"
              width={68}
              height={68}
              className="object-contain"
            />
          </div>

          <Grid xs={1} md={1} lg={2} className="!gap-8">
            <Grid.Item colSpanLG={landing ? 2 : 1}>
              <Input
                label={t('components.organisms.contactForm.nameLabel')}
                requiredMessage={t(
                  'components.organisms.contactForm.requiredMessage'
                )}
                name="name"
                type="text"
                placeholder={t(
                  'components.organisms.contactForm.namePlaceholder'
                )}
              />
            </Grid.Item>

            <Grid.Item colSpanLG={landing ? 2 : 1}>
              <Input
                label={t('components.organisms.contactForm.lastNameLabel')}
                name="lastName"
                type="text"
                placeholder={t(
                  'components.organisms.contactForm.lastNamePlaceholder'
                )}
                requiredMessage={t(
                  'components.organisms.contactForm.requiredMessage'
                )}
              />
            </Grid.Item>

            <Grid.Item colSpanLG={landing ? 2 : 1}>
              <Input
                label={t('components.organisms.contactForm.emailLabel')}
                name="email"
                type="email"
                placeholder={t(
                  'components.organisms.contactForm.emailPlaceholder'
                )}
                requiredMessage={t(
                  'components.organisms.contactForm.requiredMessage'
                )}
              />
            </Grid.Item>

            <Grid.Item colSpanLG={landing ? 2 : 1}>
              <Input
                label={t('components.organisms.contactForm.emailConfirmLabel')}
                name="emailConfirm"
                type="email"
                placeholder={t(
                  'components.organisms.contactForm.emailPlaceholder'
                )}
                requiredMessage={t(
                  'components.organisms.contactForm.emailConfirmationRequiredMessage'
                )}
              />
            </Grid.Item>

            <Grid.Item colSpanLG={landing ? 2 : 1}>
              <PhoneInput
                label={t('components.organisms.contactForm.phoneLabel')}
                name="telephone"
                language={lang === 'es' ? 'es' : 'eng'}
                requiredMessage={t(
                  'components.organisms.contactForm.requiredMessage'
                )}
                className="h-full"
              />
            </Grid.Item>

            <Grid.Item
              colSpanLG={landing ? 2 : 1}
              className={landing ? 'order-last' : ''}
            >
              <Select
                label={t('components.organisms.contactForm.hearAboustUs')}
                name="hearUs"
                defaultValue={hearUsDefaultValue}
                {...hearUsSelect}
              />
            </Grid.Item>

            <Grid.Item colSpanLG={landing ? 2 : 1}>
              <Select
                label={t('components.organisms.contactForm.buildingLabel')}
                name="building"
                requiredMessage={t(
                  'components.organisms.contactForm.requiredMessage'
                )}
                elements={buildingsSelectValues}
                defaultValue={defaultBuilding}
              />
            </Grid.Item>

            <Grid.Item colSpanLG={landing ? 2 : 1}>
              <Select
                label={t('components.organisms.contactForm.accommodationLabel')}
                name="accommodation"
                defaultValue={defaultApartment}
                elements={
                  mapOfAccommodation.get(parseInt(watchBuilding?.toString())) ??
                  []
                }
              />
            </Grid.Item>

            {!landing && (
              <Grid.Item colSpanLG={2}>
                <TextArea
                  label={t('components.organisms.contactForm.addMore')}
                  rows={5}
                  name="addMore"
                  placeholder={t(
                    'components.organisms.contactForm.addMorePlaceholder'
                  )}
                />
              </Grid.Item>
            )}
          </Grid>

          <div className="flex w-full flex-col justify-around gap-3">
            <p className="mb-1 text-[9px] uppercase tracking-wider">
              {t('components.organisms.contactForm.obligatoryFields')}
            </p>

            <Checkbox
              text={t('components.organisms.contactForm.checkboxPolicy')}
              name="checkboxMandatory"
            />

            <Checkbox
              text={t('components.organisms.contactForm.checkboxSubscription')}
              name="checkboxNotMandatory"
            />
          </div>

          <Button
            type="submit"
            size="large"
            variant="primary"
            className="md:ml-auto md:max-w-max"
          >
            {t('components.organisms.contactForm.sendButton')}
          </Button>

          <Prose
            html={textPolicies}
            className="text-other--detail text-xs leading-4 !text-primary-200"
          />
        </form>
      </FormProvider>

      <PopUp
        state={showModal}
        funcionOnClose={close}
        className="bg-surface-01 text-center"
      >
        <div className="flex max-w-md flex-col items-center justify-center gap-8">
          <Icon
            width="57"
            className={hasError ? 'text-status-error' : 'text-status-success'}
          />

          <div>
            <p className="text-heading-mobile--h3 mb-4 lg:text-heading-mobile--h2">
              {hasError
                ? t('components.organisms.contactForm.popUp.error.title')
                : t('components.organisms.contactForm.popUp.sucess.title')}
            </p>

            <p className="text-body--primary">
              {hasError
                ? t('components.organisms.contactForm.popUp.error.desc')
                : t('components.organisms.contactForm.popUp.sucess.desc')}
            </p>
          </div>

          <Button
            size="small"
            variant="secondary"
            onClick={close}
            className="max-w-max"
          >
            {hasError
              ? t('components.organisms.contactForm.popUp.error.button')
              : t('components.organisms.contactForm.popUp.sucess.button')}
          </Button>
        </div>
      </PopUp>
    </>
  );
};

ContactForm.displayName = 'ContactForm';
